import {
  Cached as CachedIcon,
  Close as CloseIcon,
  Done as DoneIcon,
  KeyboardArrowLeft,
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import React, { ReactNode } from "react";
import { LoadingButton } from "../../../common/components/LoadingButton";
import { NetworkRequest } from "../../../common/models";
import { MutationOrNetworkState } from "../../../common/models/NetworkRequest";

interface ProgressFooterProps {
  content?: ReactNode | string;
  actions?: ReactNode | string;
  saveRequests?: MutationOrNetworkState[] | MutationOrNetworkState;
}
export const ProgressFooter = ({ content, actions }: ProgressFooterProps) => {
  return (
    <div className="progress-footer">
      {content && <span className="progress-footer-content">{content}</span>}
      {actions && <span className="progress-footer-actions">{actions}</span>}
    </div>
  );
};

type ProgressFooterSaveProps = {
  request?: MutationOrNetworkState | MutationOrNetworkState[];
};

export const ProgressFooterSaveNotification: React.FC<ProgressFooterSaveProps> = ({ request }) => {
  if (!request) {
    return null;
  }

  if (!Array.isArray(request)) {
    request = [request];
  }

  const unstarted = NetworkRequest.areUnstarted(...request);
  const anyErrors = NetworkRequest.anyFailed(...request);
  const running = NetworkRequest.anyRunning(...request);

  if (unstarted) {
    return null;
  }

  return (
    <>
      <span className="progress-footer-save-icon">
        {running ? <CachedIcon /> : anyErrors ? <CloseIcon /> : <DoneIcon />}
      </span>
      <span className="progress-footer-save-text">
        {running ? "Saving" : anyErrors ? "Error Saving" : "Saved"}
      </span>
    </>
  );
};

type ProgressFooterBackProps = {
  label?: React.ReactNode;
};

export const ProgressFooterBack: React.FC<ProgressFooterBackProps> = ({ label = "Back" }) => {
  return (
    <span className="flex-center-vertical">
      <KeyboardArrowLeft /> {label}
    </span>
  );
};

interface ProgressFooterActionsProps {
  nextContent?: ReactNode | string;
  nextLoading?: boolean;
  nextOnClick?: () => void;
  nextUrl?: string;
  nextTooltip?: string;
  nextDisabledTooltip?: string;
  nextDisabled?: boolean;
  nextForm?: string;
  nextType?: "button" | "submit" | "reset";
  hideNext?: boolean;
  prevContent?: ReactNode | string;
  prevTooltip?: string;
  prevDisabledTooltip?: string;
  prevOnClick?: () => void;
  prevUrl?: string;
  prevDisabled?: boolean;
  prevLoading?: boolean;
}
export const ProgressFooterActions = ({
  nextContent,
  nextOnClick,
  nextUrl,
  nextTooltip,
  nextDisabled = false,
  nextLoading = false,
  nextDisabledTooltip,
  nextForm,
  nextType,
  prevContent = (
    <span className="flex-center-vertical">
      <KeyboardArrowLeft /> Back
    </span>
  ),
  prevTooltip,
  prevDisabledTooltip,
  prevOnClick,
  prevUrl,
  prevDisabled = false,
  prevLoading = false,
  hideNext = false,
}: ProgressFooterActionsProps) => {
  if (nextDisabled && nextDisabledTooltip) {
    nextTooltip = nextDisabledTooltip;
  }

  if (prevDisabled && prevDisabledTooltip) {
    prevTooltip = prevDisabledTooltip;
  }

  const prevComponent = prevDisabled && prevTooltip ? "div" : undefined;
  const nextComponent = nextDisabled && nextTooltip ? "div" : undefined;

  const hasPrevAction = Boolean(prevOnClick || prevUrl);
  const prevButton = hasPrevAction && prevContent && (
    <LoadingButton
      className="progress-footer-actions--prev-btn mr-md min-width-256"
      color="primary"
      onClick={prevDisabled ? undefined : prevOnClick}
      href={prevUrl}
      disabled={prevDisabled}
      size="large"
      variant="text"
      loading={prevLoading}
      component={prevComponent}
    >
      {prevContent}
    </LoadingButton>
  );

  const hasNextAction = Boolean(nextOnClick || nextUrl || nextType === "submit");
  const nextButton =
    hasNextAction && nextContent && nextComponent ? (
      <LoadingButton
        className="progress-footer-actions--next-btn min-width-256"
        color="primary"
        variant="contained"
        onClick={nextDisabled ? undefined : nextOnClick}
        href={nextUrl}
        disabled={nextDisabled}
        loading={nextLoading}
        size="large"
        component={nextComponent}
      >
        {nextContent}
      </LoadingButton>
    ) : (
      <LoadingButton
        className="progress-footer-actions--next-btn min-width-256"
        color="primary"
        variant="contained"
        onClick={nextDisabled ? undefined : nextOnClick}
        href={nextUrl}
        disabled={nextDisabled}
        loading={nextLoading}
        size="large"
        type={nextType}
        form={nextForm}
      >
        {nextContent}
      </LoadingButton>
    );

  return (
    <>
      {prevButton &&
        (prevTooltip ? (
          <Tooltip
            title={prevTooltip}
            placement="top-end"
            // pre-line preserves intentional \\n in tooltip copy
            componentsProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
          >
            <span>{prevButton}</span>
          </Tooltip>
        ) : (
          prevButton
        ))}
      {!hideNext &&
        nextButton &&
        (nextTooltip ? (
          <Tooltip
            title={nextTooltip}
            placement="top-end"
            // pre-line preserves intentional \\n in tooltip copy
            componentsProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
          >
            <span>{nextButton}</span>
          </Tooltip>
        ) : (
          nextButton
        ))}
    </>
  );
};
